import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, DollarSign, Users, Home } from 'lucide-react';
import { useHouseholdStore } from '../../store/useStore.js';

const STEPS = [
  { id: 'household', title: 'Household Info', icon: Users },
  { id: 'finances', title: 'Financial Profile', icon: DollarSign },
  { id: 'goals', title: 'Homebuying Goals', icon: Home },
  { id: 'complete', title: 'Complete', icon: CheckCircle }
];

export default function OnboardingFlow({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Household info
    household_name: '',
    
    // Financial profile
    annual_income: '',
    credit_score_1: '',
    credit_score_2: '',
    current_savings: '',
    monthly_debt_payments: '',
    debt_details: {
      studentLoans: '',
      carPayment: '',
      creditCards: '',
      other: ''
    },
    employment_type: 'w2',
    is_first_time_buyer: true,
    
    // Goals
    target_down_payment: '',
    preapproval_amount: ''
  });
  const [errors, setErrors] = useState({});
  
  const { updateProfile, isLoading } = useHouseholdStore();

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const updateDebtDetails = (field, value) => {
    setFormData(prev => ({
      ...prev,
      debt_details: {
        ...prev.debt_details,
        [field]: value
      }
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!formData.household_name.trim()) {
        newErrors.household_name = 'Household name is required';
      }
    } else if (step === 1) {
      if (!formData.annual_income || formData.annual_income <= 0) {
        newErrors.annual_income = 'Annual income is required';
      }
      if (!formData.current_savings || formData.current_savings < 0) {
        newErrors.current_savings = 'Current savings is required';
      }
    } else if (step === 2) {
      if (!formData.target_down_payment || formData.target_down_payment <= 0) {
        newErrors.target_down_payment = 'Target down payment is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    try {
      // Calculate total monthly debt
      const totalDebt = Object.values(formData.debt_details)
        .reduce((sum, val) => sum + (parseInt(val) || 0), 0);
      
      const profileData = {
        ...formData,
        annual_income: parseInt(formData.annual_income),
        credit_score_1: formData.credit_score_1 ? parseInt(formData.credit_score_1) : null,
        credit_score_2: formData.credit_score_2 ? parseInt(formData.credit_score_2) : null,
        current_savings: parseInt(formData.current_savings),
        target_down_payment: parseInt(formData.target_down_payment),
        monthly_debt_payments: totalDebt,
        debt_details: JSON.stringify(formData.debt_details),
        preapproval_amount: formData.preapproval_amount ? parseInt(formData.preapproval_amount) : null
      };

      await updateProfile(profileData);
      onComplete();
    } catch (error) {
      setErrors({ submit: error.message });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What should we call your household?
              </label>
              <input
                type="text"
                value={formData.household_name}
                onChange={(e) => updateFormData('household_name', e.target.value)}
                placeholder="The Smith Family"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {errors.household_name && (
                <p className="text-red-600 text-sm mt-1">{errors.household_name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Are you first-time homebuyers?
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.is_first_time_buyer === true}
                    onChange={() => updateFormData('is_first_time_buyer', true)}
                    className="mr-2"
                  />
                  Yes, this is our first home
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.is_first_time_buyer === false}
                    onChange={() => updateFormData('is_first_time_buyer', false)}
                    className="mr-2"
                  />
                  No, we've owned before
                </label>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Combined Annual Income (Gross)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  value={formData.annual_income}
                  onChange={(e) => updateFormData('annual_income', e.target.value)}
                  placeholder="75000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              {errors.annual_income && (
                <p className="text-red-600 text-sm mt-1">{errors.annual_income}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Score (Person 1)
                </label>
                <input
                  type="number"
                  value={formData.credit_score_1}
                  onChange={(e) => updateFormData('credit_score_1', e.target.value)}
                  placeholder="750"
                  min="300"
                  max="850"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Score (Person 2)
                </label>
                <input
                  type="number"
                  value={formData.credit_score_2}
                  onChange={(e) => updateFormData('credit_score_2', e.target.value)}
                  placeholder="720"
                  min="300"
                  max="850"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Savings for Down Payment
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  value={formData.current_savings}
                  onChange={(e) => updateFormData('current_savings', e.target.value)}
                  placeholder="8000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              {errors.current_savings && (
                <p className="text-red-600 text-sm mt-1">{errors.current_savings}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Monthly Debt Payments
              </label>
              <div className="space-y-3">
                <div>
                  <input
                    type="number"
                    value={formData.debt_details.studentLoans}
                    onChange={(e) => updateDebtDetails('studentLoans', e.target.value)}
                    placeholder="Student loans (monthly)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={formData.debt_details.carPayment}
                    onChange={(e) => updateDebtDetails('carPayment', e.target.value)}
                    placeholder="Car payment (monthly)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={formData.debt_details.creditCards}
                    onChange={(e) => updateDebtDetails('creditCards', e.target.value)}
                    placeholder="Credit card minimums (monthly)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={formData.debt_details.other}
                    onChange={(e) => updateDebtDetails('other', e.target.value)}
                    placeholder="Other loans (monthly)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employment Type
              </label>
              <select
                value={formData.employment_type}
                onChange={(e) => updateFormData('employment_type', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="w2">W2 Employee</option>
                <option value="self-employed">Self-Employed</option>
                <option value="mixed">Mixed (W2 + Self-Employed)</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Down Payment Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  value={formData.target_down_payment}
                  onChange={(e) => updateFormData('target_down_payment', e.target.value)}
                  placeholder="15000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              {errors.target_down_payment && (
                <p className="text-red-600 text-sm mt-1">{errors.target_down_payment}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre-approval Amount (Optional)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  value={formData.preapproval_amount}
                  onChange={(e) => updateFormData('preapproval_amount', e.target.value)}
                  placeholder="280000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                If you haven't gotten pre-approved yet, that's okay! You can add this later.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="text-center space-y-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                You're All Set! 🎉
              </h3>
              <p className="text-gray-600">
                Your household profile is complete. You can now start analyzing houses and tracking your progress!
              </p>
            </div>
            
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">{errors.submit}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8 pt-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors
                    ${isActive ? 'bg-indigo-600 border-indigo-600 text-white' : ''}
                    ${isCompleted ? 'bg-green-500 border-green-500 text-white' : ''}
                    ${!isActive && !isCompleted ? 'border-gray-300 text-gray-400' : ''}
                  `}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`
                      w-12 h-0.5 mx-2 transition-colors
                      ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            {STEPS.map((step, index) => (
              <div key={step.id} className="text-xs text-gray-600 text-center" style={{ width: '60px' }}>
                {step.title}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {STEPS[currentStep].title}
            </h2>
            <p className="text-gray-600">
              {currentStep === 0 && "Let's start with some basic information about your household."}
              {currentStep === 1 && "Now let's understand your financial situation."}
              {currentStep === 2 && "Finally, tell us about your homebuying goals."}
              {currentStep === 3 && ""}
            </p>
          </div>

          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>

            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Next
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
              >
                {isLoading ? 'Saving...' : 'Complete Setup'}
                <CheckCircle className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}